import React, { lazy, Suspense } from 'react';
import { AppProvider } from './context/AppContext';
import { DarkModeProvider } from './context/DarkModeContext';
import MobileNav from './components/MobileNav';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import theme from './theme';
import './index.css';

// Always-eager: landing, auth, and layout-critical pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';

// Lazy-loaded: everything else
const Subject = lazy(() => import('./pages/Subject'));
const PastPapers = lazy(() => import('./pages/PastPapers'));
const AITutor = lazy(() => import('./pages/AITutor'));
const StudyPlanner = lazy(() => import('./pages/StudyPlanner'));
const PredictedPaper = lazy(() => import('./pages/PredictedPaper'));
const Flashcards = lazy(() => import('./pages/Flashcards'));
const LectureMode = lazy(() => import('./pages/LectureMode'));
const Analytics = lazy(() => import('./pages/Analytics'));
const TopicSearch = lazy(() => import('./pages/TopicSearch'));
const Notes = lazy(() => import('./pages/Notes'));
const CustomSubject = lazy(() => import('./pages/CustomSubject'));
const ScoreTracker = lazy(() => import('./pages/ScoreTracker'));
const Pomodoro = lazy(() => import('./pages/Pomodoro'));
const MasteryCard = lazy(() => import('./pages/MasteryCard'));
const Help = lazy(() => import('./pages/Help'));
const Admin = lazy(() => import('./pages/Admin'));
const Lesson = lazy(() => import('./pages/Lesson'));
const DiagnosticTest = lazy(() => import('./pages/DiagnosticTest'));
const Community = lazy(() => import('./pages/Community'));
const Admissions = lazy(() => import('./pages/Admissions'));
const MemoryPage = lazy(() => import('./pages/MemoryPage'));
const QuestionRush = lazy(() => import('./pages/QuestionRush'));
const QuestionBank = lazy(() => import('./pages/QuestionBank'));
const QuizMode = lazy(() => import('./pages/QuizMode'));
const ReviewQueue = lazy(() => import('./pages/ReviewQueue'));
const Settings = lazy(() => import('./pages/Settings'));
const ClassroomDashboard = lazy(() => import('./pages/ClassroomDashboard'));
const JoinClassroom = lazy(() => import('./pages/JoinClassroom'));
const Mistakes = lazy(() => import('./pages/Mistakes'));
const Assignments = lazy(() => import('./pages/Assignments'));
const Profile = lazy(() => import('./pages/Profile'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const Changelog = lazy(() => import('./pages/Changelog'));
const Focus = lazy(() => import('./pages/Focus'));
const Feedback = lazy(() => import('./pages/Feedback'));
const Integrations = lazy(() => import('./pages/Integrations'));
const CailynLive = lazy(() => import('./pages/CailynLive'));
const LiveHistory = lazy(() => import('./pages/LiveHistory'));
const Pricing = lazy(() => import('./pages/Pricing'));
const TestPrep = lazy(() => import('./pages/TestPrep'));
const SummerSchool = lazy(() => import('./pages/SummerSchool'));
const ExamSimulator = lazy(() => import('./pages/ExamSimulator'));

const PageLoader = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: theme.bg }}>
    <div style={{ width: 36, height: 36, border: `3px solid ${theme.borderSoft}`, borderTopColor: theme.primary, borderRadius: '50%', animation: 'spin 0.75s linear infinite' }} />
  </div>
);

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  return user ? children : <Navigate to="/login" />;
};

const EducatorRoute = ({ children }) => {
  const { user } = useAuth();
  if (user?.accountType !== 'educator') return <Navigate to="/dashboard" />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <AppProvider>
      <DarkModeProvider>
      <Router>
        <MobileNav />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/subject/:subjectId" element={<PrivateRoute><Subject /></PrivateRoute>} />
            <Route path="/past-papers" element={<PrivateRoute><PastPapers /></PrivateRoute>} />
            <Route path="/predicted/:subjectId" element={<PrivateRoute><PredictedPaper /></PrivateRoute>} />
            <Route path="/flashcards" element={<PrivateRoute><Flashcards /></PrivateRoute>} />
            <Route path="/flashcards/:subjectId" element={<PrivateRoute><Flashcards /></PrivateRoute>} />
            <Route path="/planner" element={<PrivateRoute><StudyPlanner /></PrivateRoute>} />
            <Route path="/tutor" element={<PrivateRoute><AITutor /></PrivateRoute>} />
            <Route path="/tutor/:subjectId" element={<PrivateRoute><AITutor /></PrivateRoute>} />
            <Route path="/analytics" element={<PrivateRoute><Analytics /></PrivateRoute>} />
            <Route path="/search" element={<PrivateRoute><TopicSearch /></PrivateRoute>} />
            <Route path="/lecture" element={<PrivateRoute><LectureMode /></PrivateRoute>} />
            <Route path="/lecture/:subjectId" element={<PrivateRoute><LectureMode /></PrivateRoute>} />
            <Route path="/notes" element={<PrivateRoute><Notes /></PrivateRoute>} />
            <Route path="/notes/:subjectId" element={<PrivateRoute><Notes /></PrivateRoute>} />
            <Route path="/custom-subject" element={<PrivateRoute><CustomSubject /></PrivateRoute>} />
            <Route path="/custom-subject/:subjectId" element={<PrivateRoute><CustomSubject /></PrivateRoute>} />
            <Route path="/scores" element={<PrivateRoute><ScoreTracker /></PrivateRoute>} />
            <Route path="/help" element={<PrivateRoute><Help /></PrivateRoute>} />
            <Route path="/quiz/:subjectId" element={<PrivateRoute><QuizMode /></PrivateRoute>} />
            <Route path="/admin" element={<PrivateRoute><Admin /></PrivateRoute>} />
            <Route path="/lesson/:subjectId/:topicId" element={<PrivateRoute><Lesson /></PrivateRoute>} />
            <Route path="/diagnostic" element={<PrivateRoute><DiagnosticTest /></PrivateRoute>} />
            <Route path="/diagnostic/:subjectId" element={<PrivateRoute><DiagnosticTest /></PrivateRoute>} />
            <Route path="/community" element={<PrivateRoute><Community /></PrivateRoute>} />
            <Route path="/rush/:subjectId" element={<PrivateRoute><QuestionRush /></PrivateRoute>} />
            <Route path="/bank/:subjectId" element={<PrivateRoute><QuestionBank /></PrivateRoute>} />
            <Route path="/bank" element={<PrivateRoute><QuestionBank /></PrivateRoute>} />
            <Route path="/rush" element={<PrivateRoute><QuestionRush /></PrivateRoute>} />
            <Route path="/memory" element={<PrivateRoute><MemoryPage /></PrivateRoute>} />
            <Route path="/admissions" element={<PrivateRoute><Admissions /></PrivateRoute>} />
            <Route path="/classroom" element={<PrivateRoute><EducatorRoute><ClassroomDashboard /></EducatorRoute></PrivateRoute>} />
            <Route path="/join" element={<PrivateRoute><JoinClassroom /></PrivateRoute>} />
            <Route path="/review" element={<PrivateRoute><ReviewQueue /></PrivateRoute>} />
            <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
            <Route path="/assignments" element={<PrivateRoute><Assignments /></PrivateRoute>} />
            <Route path="/mistakes" element={<PrivateRoute><Mistakes /></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
            <Route path="/leaderboard" element={<PrivateRoute><Leaderboard /></PrivateRoute>} />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="/focus" element={<PrivateRoute><Focus /></PrivateRoute>} />
            <Route path="/feedback" element={<PrivateRoute><Feedback /></PrivateRoute>} />
            <Route path="/integrations" element={<PrivateRoute><Integrations /></PrivateRoute>} />
            <Route path="/live" element={<PrivateRoute><CailynLive /></PrivateRoute>} />
            <Route path="/live/history" element={<PrivateRoute><LiveHistory /></PrivateRoute>} />
            <Route path="/live/:subjectId" element={<PrivateRoute><CailynLive /></PrivateRoute>} />
            <Route path="/pomodoro" element={<PrivateRoute><Pomodoro /></PrivateRoute>} />
            <Route path="/mastery-card" element={<PrivateRoute><MasteryCard /></PrivateRoute>} />
            <Route path="/test-prep" element={<PrivateRoute><TestPrep /></PrivateRoute>} />
            <Route path="/summer" element={<PrivateRoute><SummerSchool /></PrivateRoute>} />
            <Route path="/exam" element={<PrivateRoute><ExamSimulator /></PrivateRoute>} />
            <Route path="/exam/:examId" element={<PrivateRoute><ExamSimulator /></PrivateRoute>} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </Router>
      </DarkModeProvider>
      </AppProvider>
    </AuthProvider>
  );
}

export default App;
